import { animate } from 'motion'

function insertConfirmModal(message, element) {
  const textConfirmation = element.dataset.turboConfirmTextConfirmation
  const requiresTextConfirmation = !!textConfirmation

  const textConfirmationHtml = requiresTextConfirmation ? `
    <div class="mt-4">
      <div class="block text-sm font-medium text-body mb-2.5">
        Type <span class="font-mono border border-default-medium rounded-sm px-1.5 py-0.5 text-heading">${textConfirmation}</span> to confirm:
      </div>
      <input
        data-behavior="text-confirmation"
        type="text"
        placeholder="Type ${textConfirmation} to confirm"
        autocomplete="off"
        class="w-full text-sm rounded-base bg-neutral-secondary-medium border border-default-medium text-heading px-3 py-2 focus:ring-2 focus:ring-brand-soft focus:border-brand"
        placeholder="${textConfirmation}"
      />
    </div>
  ` : ''

  let content = `
    <div id="confirm-backdrop" class="confirm-backdrop opacity-0 bg-dark-backdrop/50 fixed inset-0 z-40"></div>
    <div id="confirm-modal" tabindex="-1" class="fixed top-0 left-0 right-0 z-50 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full flex justify-center items-center">
      <div class="relative w-full max-w-md max-h-full">
        <div class="relative flex flex-col gap-4 bg-neutral-primary-soft border border-default rounded-base shadow-sm p-4 md:p-6">
          <div class="text-left">
            <h1 class="text-xl font-semibold text-heading mb-2">
              ${element.dataset.turboConfirmTitle || "Confirmation"}
            </h1>
            <h3 class="font-normal text-body">
              ${message}
            </h3>
            ${textConfirmationHtml}
          </div>
          <div class="flex gap-3 justify-end pt-2">
            <button data-behavior="cancel" type="button" class="text-sm font-medium rounded-base inline-flex items-center px-5 py-2.5 text-center text-body bg-neutral-secondary-medium box-border border border-default-medium hover:bg-neutral-tertiary-medium hover:text-heading focus:ring-4 focus:ring-neutral-tertiary shadow-xs">
              ${element.dataset.turboConfirmCancelLabel || "Cancel"}
            </button>
            <button data-behavior="commit" type="button" ${requiresTextConfirmation ? 'disabled' : ''} class="text-sm font-medium rounded-base inline-flex items-center px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed ${element.dataset.turboConfirmConfirmButtonClass || "text-white bg-danger box-border border border-transparent hover:bg-danger-strong focus:ring-4 focus:ring-danger-medium shadow-xs"}">
              ${element.dataset.turboConfirmConfirmLabel || "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  `

  document.body.insertAdjacentHTML('beforeend', content);
  document.activeElement.blur();

  const backdrop = document.getElementById("confirm-backdrop");
  const dialog = document.getElementById("confirm-modal");

  // Setup text confirmation listener
  if (requiresTextConfirmation) {
    const input = dialog.querySelector("[data-behavior='text-confirmation']")
    const commitButton = dialog.querySelector("[data-behavior='commit']")

    input.addEventListener("input", () => {
      commitButton.disabled = input.value !== textConfirmation
    })

    // Focus the input
    requestAnimationFrame(() => input.focus())
  }

  // Animate in
  animate(backdrop, { opacity: [0, 1] }, { duration: 0.3, easing: 'ease-out' })
  dialog.style.transform = 'scale(0.95)'
  dialog.style.opacity = '0'
  animate(dialog, { opacity: [0, 1], transform: ['scale(0.95)', 'scale(1)'] }, { duration: 0.2, easing: 'ease-out' })

  return { backdrop, dialog };
}

async function animateOut(backdrop, dialog) {
  const animations = []

  if (backdrop) {
    animations.push(
      animate(backdrop, { opacity: [1, 0] }, { duration: 0.2, easing: 'ease-in' }).finished
    )
  }

  animations.push(
    animate(dialog, { opacity: [1, 0], transform: ['scale(1)', 'scale(0.95)'] }, { duration: 0.2, easing: 'ease-in' }).finished
  )

  await Promise.all(animations)

  backdrop?.remove()
  dialog.remove()
}

export function setupConfirmationModal(Turbo) {
  Turbo.config.forms.confirm = (message, element) => {
    const { backdrop, dialog } = insertConfirmModal(message, element);

    return new Promise((resolve) => {
      dialog.querySelector("[data-behavior='cancel']").addEventListener("click", async () => {
        await animateOut(backdrop, dialog)
        resolve(false)
      }, { once: true })

      dialog.querySelector("[data-behavior='commit']").addEventListener("click", async () => {
        await animateOut(backdrop, dialog)
        resolve(true)
      }, { once: true })
    })
  }
}